import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, notification } from 'antd';
import Cookies from 'js-cookie';

import { Button } from 'components/Atom/Button';
import { getCubejsToken } from 'utils/cubejs';
import { setAccessToken, setRefreshToken } from 'utils/storage';
import storage from 'utils/storage';
import { authSdk } from 'lib/auth';
import { isWebAuthnSupported, setPasskeyEmail } from 'postex-auth-sdk-stage';
import './assets/style.css';

const toast = (args) => {
  const fn = args.variant === 'destructive' ? notification.error : notification.success;
  fn({ message: args.title, description: args.description, placement: 'bottomRight' });
};

const LoginPageShell = ({ heading, subtext, children }) => {
  const currentYear = new Date().getFullYear();

  return (
    <div className='login-wrapper'>
      <div className='login-form login-bg-wrapper'>
        <div className='form-wrapper'>
          <img className='logo' alt='Xpay logo' src='/XPay.svg' />
          <div className='welcome-text'>
            <p className='heading'>{heading}</p>
            <div className='subtext'>{subtext}</div>
          </div>
          {children}
        </div>

        <div className='form-footer-note pb-6'>
          <div className='copyright'>
            <a href='https://xpay.postexglobal.com' target='_blank' rel='noopener noreferrer' className='pr-2 hover:underline' style={{ color: '#4f4d55' }}>
              ©{currentYear} PostEx.
            </a>{' '}
          </div>
          <p className='terms'>
            <a href='https://xpay.postexglobal.com' target='_blank' rel='noopener noreferrer' className='pr-2 hover:underline'>
              Terms of Service
            </a>
            |
            <a href='https://xpay.postexglobal.com' target='_blank' rel='noopener noreferrer' className='pl-2 hover:underline'>
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
      <div>
        <img src='/loginImage.svg' alt='image' />
      </div>
    </div>
  );
};

const VerifyMagicLink = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [redirectNow, setRedirectNow] = useState(false);
  const [showAddPasskeyPrompt, setShowAddPasskeyPrompt] = useState(false);
  const [authResponse, setAuthResponse] = useState(null);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const verificationStartedRef = useRef(false);
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    if (redirectNow) {
      navigate('/', { replace: true, state: { pathname: '/auth/login', loginSuccess: true } });
    }
  }, [redirectNow, navigate]);

  useEffect(() => {
    if (verificationStartedRef.current) return;
    verificationStartedRef.current = true;

    const verifyMagicLink = async () => {
      if (!token) {
        setError('Invalid magic link. Missing token.');
        setLoading(false);
        return;
      }

      try {
        await authSdk.verifyMagicLink(token);
        const response = await authSdk.completeMagicLink();
        setAuthResponse(response);
        setShowAddPasskeyPrompt(true);
      } catch (err) {
        const msg = err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Failed to verify magic link';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    verifyMagicLink();
  }, [token]);

  const onAuthSuccess = async (data) => {
    if (!data?.access_token) throw new Error('No token returned from login API');

    setAccessToken(data.access_token);
    if (data.refresh_token) setRefreshToken(data.refresh_token);
    window.localStorage.setItem('name', JSON.stringify(data?.name ?? 'Admin'));
    window.localStorage.setItem('email', data?.email ?? '');
    if (data?.realm != null) {
      window.localStorage.setItem('realm', data.realm);
      Cookies.set('accountId', data.realm, { expires: 99 });
    }
    if (data?.apps != null) storage.setApps(data.apps);
    setRedirectNow(true);
    getCubejsToken();
  };

  const registerPasskeyForEmail = async () => {
    if (!isWebAuthnSupported()) {
      throw new Error('WebAuthn is not supported in this browser');
    }

    const verifiedEmail = authResponse?.email;
    if (!verifiedEmail) {
      throw new Error('Email is required to register a passkey');
    }

    await authSdk.registerPasskey(verifiedEmail);
    await setPasskeyEmail(verifiedEmail);
  };

  const handleSkipAddPasskey = async () => {
    if (!authResponse) return;

    await onAuthSuccess(authResponse);
    setAuthResponse(null);
    setShowAddPasskeyPrompt(false);
  };

  const handleAddPasskeyAfterVerification = async () => {
    if (!authResponse) return;

    setPasskeyLoading(true);
    try {
      await registerPasskeyForEmail();
      await onAuthSuccess(authResponse);
      setAuthResponse(null);
      setShowAddPasskeyPrompt(false);
    } catch (err) {
      if (err?.name === 'InvalidStateError') {
        toast({
          title: 'Passkey already exists',
          description: 'This device is already registered.',
        });
      } else if (err?.name === 'NotAllowedError') {
        toast({
          title: 'Passkey creation cancelled',
          description: 'You can create a passkey later from this device.',
        });
      } else {
        const msg = err?.response?.data?.message || err?.message || 'Passkey registration failed';
        toast({
          title: 'Passkey registration failed',
          description: msg,
          variant: 'destructive',
        });
      }
      await onAuthSuccess(authResponse);
      setAuthResponse(null);
      setShowAddPasskeyPrompt(false);
    } finally {
      setPasskeyLoading(false);
    }
  };

  if (loading) {
    return (
      <LoginPageShell heading='Verifying your magic link...' subtext='Please wait while we verify your authentication.'>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <span className='magic-link-spinner' aria-label='Verifying magic link' />
        </div>
      </LoginPageShell>
    );
  }

  if (error) {
    return (
      <LoginPageShell heading='Verification Failed' subtext={error}>
        <Form layout='vertical'>
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'right', marginTop: '8px' }}>
              <Button block className='button-theme rounded-lg' onClick={() => navigate('/auth/login', { replace: true })}>
                Go to Login
              </Button>
            </div>
          </Form.Item>
        </Form>
      </LoginPageShell>
    );
  }

  if (showAddPasskeyPrompt) {
    return (
      <LoginPageShell heading='Add a Passkey?' subtext='Add a passkey to sign in faster next time using Face ID, Touch ID, or Windows Hello.'>
        <Form layout='vertical'>
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'right', marginTop: '8px' }}>
              <Button block className='button-theme rounded-lg' onClick={handleAddPasskeyAfterVerification} loading={passkeyLoading}>
                Add Passkey
              </Button>
            </div>
          </Form.Item>
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '4px' }}>
              <Button className='text-button' type='button' disabled={passkeyLoading} onClick={handleSkipAddPasskey}>
                Skip for now
              </Button>
            </div>
          </Form.Item>
        </Form>
      </LoginPageShell>
    );
  }

  return null;
};

export default VerifyMagicLink;
